import * as React from 'react';
import { Card } from './Card';
import { Badge } from './Badge';
import { DocumentIconTile, DocumentListItem } from './DocumentListItem';
import { cn } from '../utils';
import { formatMoney } from '@insio/decimal';
import { useLocale, useTranslation } from '../providers/UIProvider';
import { IconName } from '../icons/names';
import { Temporal, isPastDate, formatShortDate } from '@insio/dates';
import { Decimal } from 'decimal.js';
import { isNil } from 'es-toolkit';
import { SvgInvoice, SvgReceipt } from './generated/svg';

export type PaymentStatus = 'PAID' | 'UNPAID' | 'PARTIALLY_PAID';

export interface InvoiceListItemData {
  number: string;
  internalNumber?: string;
  vendor: string;
  /** Supplier VAT id (DIČ) — snapshot stored on the invoice. */
  vendorVatId?: string;
  customer?: string;
  /** Free-text invoice description. */
  description?: string;
  amount: Decimal;
  /** Total amount excluding VAT — shown underneath the (incl. VAT) amount. */
  amountNoVat?: Decimal;
  currency: string;
  issueDate: Temporal.PlainDate;
  dueDate: Temporal.PlainDate;
  validationStatus?: string;
  /**
   * Composite status: FOR_EDIT | FOR_APPROVAL | FOR_EXPORT | FOR_PAYMENT | FOR_EXPORT_AND_PAYMENT |
   * REJECTED | COMPLETED | ARCHIVED.
   */
  compositeStatus?: string;
  documentType?: string;
  /** Payment status of the invoice. When provided, overdue styling is only applied to unpaid items. */
  paymentStatus?: PaymentStatus | string;
}

/** An invoice amount converted to another currency. */
export interface ConvertedAmount {
  amount: Decimal;
  currency: string;
}

export interface InvoiceListItemProps extends Omit<React.ComponentProps<typeof Card>, 'onClick'> {
  invoice: InvoiceListItemData;
  /** The amount converted to the accounting currency, shown under the amount. */
  convertedAmount?: ConvertedAmount;
  onClick?: (invoice: InvoiceListItemData) => void;
  locKey?: string;
  /** Checkbox rendered as an absolute overlay in the top-left corner of the card. */
  checkbox?: React.ReactNode;
  /** Badge shown in place of the composite status badge, e.g. a completion badge. */
  statusBadge?: React.ReactNode;
  /** Badges shown beside the status badge, e.g. a cancellation badge; the status badge stays. */
  extraBadges?: React.ReactNode;
  /** Extra badges shown after the status badges, in both the desktop and mobile layouts. */
  badges?: React.ReactNode;
  /** Who approves the invoice, shown after the badges. */
  approvers?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// documentType → icon + colors + label key
// ---------------------------------------------------------------------------

interface DocTypeConfig {
  icon: IconName;
  customSvgIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  bg: string;
  color: string;
  labelKey: string;
}

function getDocTypeConfig(documentType?: string): DocTypeConfig {
  const type = documentType?.toUpperCase();
  switch (type) {
    case 'RECEIPT':
      return {
        icon: 'file-lines',
        customSvgIcon: SvgReceipt,
        bg: 'bg-background-muted',
        color: 'text-secondary-base',
        labelKey: 'documentType.RECEIPT'
      };
    case 'CREDIT_NOTE':
      return {
        icon: 'file-lines',
        bg: 'bg-warn-bg',
        color: 'text-warn-text',
        labelKey: 'documentType.CREDIT_NOTE'
      };
    case 'DEBIT_NOTE':
      return {
        icon: 'file-lines',
        bg: 'bg-error-bg',
        color: 'text-error-text',
        labelKey: 'documentType.DEBIT_NOTE'
      };
    case 'ADVANCE':
      return {
        icon: 'file-user',
        bg: 'bg-info-bg',
        color: 'text-info-text',
        labelKey: 'documentType.ADVANCE'
      };
    case 'PROFORMA':
      return {
        icon: 'file-user',
        bg: 'bg-info-bg',
        color: 'text-info-text',
        labelKey: 'documentType.PROFORMA'
      };
    case 'INVOICE':
      return {
        icon: 'file-invoice-dollar',
        customSvgIcon: SvgInvoice,
        bg: 'bg-background-muted',
        color: 'text-primary-base',
        labelKey: 'documentType.INVOICE'
      };
    case 'OTHER':
    default:
      return {
        icon: 'file-lines',
        bg: 'bg-background-muted',
        color: 'text-foreground-muted',
        labelKey: 'documentType.OTHER'
      };
  }
}

// ---------------------------------------------------------------------------
// Validation status badge
// ---------------------------------------------------------------------------

function ValidationStatusBadge({
  validationStatus,
  className
}: {
  validationStatus?: string;
  className?: string;
}) {
  const t = useTranslation('components.invoiceListItem');
  const label = t(`validationStatus.${validationStatus ?? 'UNPROCESSED'}`);

  const getOutline = (): 'neutral' | 'success' | 'warning' | 'error' | 'primary' => {
    switch (validationStatus) {
      case 'SUCCESS':
        return 'success';
      case 'WARNING':
        return 'warning';
      case 'ERROR':
        return 'error';
      case 'PENDING':
      case 'WAITING_FOR_CREDITS':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  return (
    <Badge outline={getOutline()} className={cn('whitespace-nowrap bg-transparent', className)}>
      {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Composite status badge
// ---------------------------------------------------------------------------

function CompositeStatusBadge({
  compositeStatus,
  className
}: {
  compositeStatus?: string;
  className?: string;
}) {
  const t = useTranslation('components.invoiceListItem');
  if (!compositeStatus) return null;
  const label = t(`compositeStatus.${compositeStatus}`);

  const getColor = (): 'success' | 'info' | 'warning' | 'neutral' | 'error' => {
    switch (compositeStatus) {
      // Waiting on somebody — the colour every other document wears while it waits.
      case 'FOR_APPROVAL':
        return 'warning';
      case 'FOR_EXPORT':
      case 'FOR_PAYMENT':
      case 'FOR_EXPORT_AND_PAYMENT':
      case 'COMPLETED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'ARCHIVED':
        return 'neutral';
      default:
        return 'info';
    }
  };

  return (
    <Badge color={getColor()} className={cn('whitespace-nowrap', className)}>
      {label}
    </Badge>
  );
}

/**
 * Validation status is only relevant while the invoice is still editable —
 * show it only for FOR_EDIT invoices whose validation is past UNPROCESSED.
 */
function shouldShowValidationStatus(invoice: InvoiceListItemData): boolean {
  return (
    invoice.compositeStatus === 'FOR_EDIT' &&
    invoice.validationStatus != null &&
    invoice.validationStatus !== 'UNPROCESSED'
  );
}

// ---------------------------------------------------------------------------
// Converted amount
// ---------------------------------------------------------------------------

function ConvertedAmountLine({ convertedAmount }: { convertedAmount?: ConvertedAmount }) {
  const t = useTranslation('components.invoiceListItem');
  if (!convertedAmount) return null;
  const amount = formatMoney(convertedAmount.amount, { currencyCode: convertedAmount.currency });
  return (
    <span
      className='text-xs text-foreground-muted tabular-nums whitespace-nowrap'
      title={t('labels.convertedAmountHint')}
    >
      {t('labels.convertedAmount', { amount })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function InvoiceListItem({
  invoice,
  convertedAmount,
  onClick,
  checkbox,
  statusBadge,
  extraBadges,
  badges,
  approvers,
  ...props
}: InvoiceListItemProps) {
  const t = useTranslation('components.invoiceListItem');
  const dateLocale = useLocale();
  const isPaid = invoice.paymentStatus?.toUpperCase() === 'PAID';
  const isOverdue = !isPaid && isPastDate(invoice.dueDate);
  const { icon, customSvgIcon, bg, color, labelKey } = getDocTypeConfig(invoice.documentType);
  const docTypeLabel = t(labelKey);
  const noVatLabel =
    invoice.amountNoVat !== undefined
      ? `${t('labels.exclVat')} ${formatMoney(invoice.amountNoVat, { currencyCode: invoice.currency })}`
      : undefined;

  return (
    <DocumentListItem
      onClick={onClick ? () => onClick(invoice) : undefined}
      checkbox={checkbox}
      icon={<DocumentIconTile icon={icon} svg={customSvgIcon} tileClass={bg} iconClass={color} />}
      iconLabel={docTypeLabel}
      number={invoice.number}
      numberAside={invoice.internalNumber}
      typeLabel={docTypeLabel}
      party={{
        name: invoice.vendor,
        vatId: invoice.vendorVatId,
        counterparty: invoice.customer
      }}
      dates={[
        {
          label: t('labels.issueDate'),
          value: formatShortDate(invoice.issueDate, dateLocale)
        },
        {
          label: t('labels.dueDate'),
          value: formatShortDate(invoice.dueDate, dateLocale),
          tone: isOverdue ? 'error' : 'default'
        }
      ]}
      // A caller's own status badge keeps its size on a narrow row; the composite one shrinks.
      leadingBadge={statusBadge}
      badges={
        <>
          {isNil(statusBadge) && <CompositeStatusBadge compositeStatus={invoice.compositeStatus} />}
          {extraBadges}
          {shouldShowValidationStatus(invoice) && (
            <ValidationStatusBadge validationStatus={invoice.validationStatus} />
          )}
          {badges}
        </>
      }
      badgesAside={approvers}
      description={invoice.description}
      amount={{
        value: formatMoney(invoice.amount, { currencyCode: invoice.currency }),
        lines: [
          noVatLabel,
          <ConvertedAmountLine key='converted' convertedAmount={convertedAmount} />
        ]
      }}
      {...props}
    />
  );
}

InvoiceListItem.displayName = 'InvoiceListItem';

export { InvoiceListItem };
